import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * Los ejes de filtro de los prompts de Dashbo en la banda: **Momento** y **Rol**.
 *
 * Viven DENTRO del plegado «Sugeridos por Dashbo»: los prompts de la agencia son una lista corta que
 * se recorre por nombre y no llevan filtros. Todo pasa sobre la data que la banda ya tiene — no hay
 * un request por filtro. Tres decisiones que no son cosméticas:
 *
 * 1. **Los momentos son los del BACKEND, que son SEIS.** El mockup dibuja cinco y con esa lista
 *    `semanal`, `cuando_cambio` y `cliente_nuevo` quedaban inalcanzables: prompts que existen, que
 *    el usuario ve en el grid sin filtrar, y que ningún chip puede aislar. La lista vive en
 *    `Prompt.MOMENTO_*` (`agent_tools/models.py`) y es la misma que usan `PromptLibrarySection` y
 *    `CreatePromptModal`.
 * 2. **Un chip en 0 se deshabilita con su `(0)`, no se esconde.** Aparecer y desaparecer se lee
 *    como un estado del botón (convención del repo, `ClientsV2Toolbar`). Y el chip PRENDIDO nunca
 *    se deshabilita, aunque su combinación dé 0: hay que poder apagarlo.
 * 3. **Los contadores son por EJE, sobre la lista completa.** Un chip dice cuántos prompts existen
 *    de ese momento (o de ese rol), no cuántos sobreviven al otro filtro. Cruzarlos haría que el
 *    número de un chip cambie al tocar el otro eje —lo que se lee como que se perdieron prompts— y
 *    dejaría el vacío de la combinación inalcanzable con el mouse; para eso está el empty state.
 *
 * Hubo un tercer eje, «Creado por» (Dashbo / la agencia). Se fue cuando los prompts de la agencia
 * pasaron a su propia lista, arriba del plegado: acá adentro ya no queda nada que partir.
 */

export const FILTER_ALL = 'all';

/** ¿Lo escribió la agencia? `AGENCY` y `CLIENT` son los dos scopes que produce una agencia —uno
 *  para todos sus clientes, el otro para uno— y los dos son SUYOS. Un scope desconocido (o ausente)
 *  cuenta como de Dashbo: el catálogo es el default, y atribuirle a la agencia un texto que no
 *  escribió es peor que no hacerlo. */
export function isAgencyPrompt(prompt) {
    return !!prompt && (prompt.scope === 'AGENCY' || prompt.scope === 'CLIENT');
}

// Los SEIS momentos del backend (`Prompt.MOMENTO_*` / `VALID_MOMENTOS` de prompt_library_views.py).
export const MOMENTOS = [
    'dia_a_dia', 'semanal', 'antes_reunion', 'cierre_mes', 'cuando_cambio', 'cliente_nuevo',
];

// Los cuatro roles del backend (`Prompt.ROLE_CHOICES`).
export const ROLES = ['media_buyer', 'analista', 'account', 'director'];

/** ¿Este prompt cae en ese momento? `all` matchea todo; un prompt sin momentos sólo cae en `all`. */
export function matchesMomento(prompt, momento) {
    if (momento === FILTER_ALL) return true;
    return (prompt.momentos || []).indexOf(momento) !== -1;
}

/** ¿Este prompt es de ese rol? Un prompt sin rol (los que se suben sin elegirlo) sólo cae en `all`. */
export function matchesRole(prompt, role) {
    if (role === FILTER_ALL) return true;
    return prompt.role === role;
}

/** La combinación de los ejes. Es lo que dibuja el grid. */
export function filterPrompts(prompts, {momento = FILTER_ALL, role = FILTER_ALL} = {}) {
    return (prompts || []).filter((p) => matchesMomento(p, momento) && matchesRole(p, role));
}

/** {valor: cantidad} de un eje, incluido `all`. */
function countsFor(prompts, values, matcher) {
    const list = prompts || [];
    const counts = {[FILTER_ALL]: list.length};
    values.forEach((value) => {
        counts[value] = list.filter((p) => matcher(p, value)).length;
    });
    return counts;
}

export function momentoCounts(prompts) {
    return countsFor(prompts, MOMENTOS, matchesMomento);
}

export function roleCounts(prompts) {
    return countsFor(prompts, ROLES, matchesRole);
}

function Chip({value, label, count, active, onSelect}) {
    return (
        <button
            type="button"
            className={`iab-cx ${active ? 'on' : ''}`}
            aria-pressed={active}
            // El prendido nunca se deshabilita: sin eso, apagar un filtro que quedó en 0 exige
            // adivinar cuál otro chip tocar para poder volver.
            disabled={!active && count === 0}
            onClick={() => onSelect(value)}
        >
            {label} <span className="iab-cx-n">({count})</span>
        </button>
    );
}

export function PromptFilters({
    prompts, momento = FILTER_ALL, role = FILTER_ALL, onMomentoChange, onRoleChange,
}) {
    const {t} = useTranslation();
    const moments = useMemo(() => momentoCounts(prompts), [prompts]);
    const roles = useMemo(() => roleCounts(prompts), [prompts]);

    // El rótulo y los chips van en dos celdas: con los chips sueltos al lado del rótulo, en un ancho
    // angosto el renglón que envuelve arrancaba DEBAJO del rótulo y el chip suelto se leía como otra
    // columna (QA-VIS-banda-22). Así los chips forman un bloque, como en el mockup de la galería.
    return (
        <div className="iab-libfilters">
            <div className="iab-libfilters-axis" role="group" aria-label={t('ia_booster.band.filter_momento')}>
                <span className="iab-lf-k">{t('ia_booster.band.filter_momento')}</span>
                <div className="iab-lf-chips">
                    <Chip value={FILTER_ALL} label={t('ia_booster.band.filter_all')} count={moments[FILTER_ALL]}
                          active={momento === FILTER_ALL} onSelect={onMomentoChange}/>
                    {MOMENTOS.map((m) => (
                        <Chip key={m} value={m} label={t(`prompt_library.momentos.${m}`)} count={moments[m]}
                              active={momento === m} onSelect={onMomentoChange}/>
                    ))}
                </div>
            </div>
            <div className="iab-libfilters-axis" role="group" aria-label={t('ia_booster.band.filter_role')}>
                <span className="iab-lf-k">{t('ia_booster.band.filter_role')}</span>
                <div className="iab-lf-chips">
                    <Chip value={FILTER_ALL} label={t('ia_booster.band.filter_all')} count={roles[FILTER_ALL]}
                          active={role === FILTER_ALL} onSelect={onRoleChange}/>
                    {ROLES.map((r) => (
                        <Chip key={r} value={r} label={t(`prompt_library.roles.${r}`)} count={roles[r]}
                              active={role === r} onSelect={onRoleChange}/>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default PromptFilters;
